import { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { Inbox, CalendarCheck, MessageSquareText, Palette, ScrollText, Blocks, LogOut, Bell, Volume2, VolumeX, Menu, Loader2, ShieldBan } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { RealtimeProvider, useRealtime } from "@/context/RealtimeContext";
import { BrandMark } from "@/components/portal/PortalShell";
import { OnlineServiceToggle, SystemOnlineToggle, DocumentValidationToggle } from "@/components/admin/OnlineServiceToggle";

const NAV = [
  { to: "/admin", label: "Solicitações", icon: Inbox, end: true, id: "requests" },
  { to: "/admin/agendamentos", label: "Agendamentos", icon: CalendarCheck, id: "appointments" },
  { to: "/admin/configuracoes/mensagens", label: "Mensagens de atendimento", icon: MessageSquareText, id: "messages", group: "Configurações" },
  { to: "/admin/configuracoes/marca", label: "Marca e atendimento", icon: Palette, id: "branding", group: "Configurações" },
  { to: "/admin/funcionalidades", label: "Funcionalidades", icon: Blocks, id: "features", group: "Configurações" },
  { to: "/admin/seguranca", label: "Segurança", icon: ShieldBan, id: "security", group: "Configurações" },
  { to: "/admin/auditoria", label: "Auditoria", icon: ScrollText, id: "audit", group: "Configurações" },
];

const Sidebar = ({ onNavigate }) => {
  const { config } = useBranding();
  return (
    <nav className="flex h-full flex-col gap-1 p-4">
      <div className="mb-8 flex items-center gap-3 px-2 pt-2">
        <BrandMark config={config} size="sm" />
        <div className="min-w-0 leading-tight">
          <p className="truncate font-display text-sm text-slate-100">{config?.org_name}</p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Operação</p>
        </div>
      </div>
      {NAV.map((n, i) => (
        <div key={n.to}>
          {n.group && NAV[i - 1]?.group !== n.group && <p className="mb-1 mt-6 px-3 text-[11px] uppercase tracking-[0.18em] text-slate-600">{n.group}</p>}
          <NavLink to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.id}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? "bg-white/[0.06] text-slate-50" : "text-slate-400 hover:bg-white/[0.03] hover:text-slate-200"}`}>
            {({ isActive }) => (<><n.icon className="h-4 w-4" style={isActive ? { color: "var(--brand)" } : undefined} />{n.label}</>)}
          </NavLink>
        </div>
      ))}
    </nav>
  );
};

const TopBar = ({ onMenu }) => {
  const { user, logout } = useAuth();
  const { status, newCount, clearNew, sound, setSound } = useRealtime();
  const open = status === "open";
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-white/5 bg-[#0D1322]/90 px-4 backdrop-blur-md sm:px-6">
      <button className="text-slate-400 lg:hidden" onClick={onMenu} data-testid="admin-menu-toggle"><Menu className="h-5 w-5" /></button>
      <span className="flex items-center gap-2 text-xs text-slate-400" data-testid="admin-ws-status">
        <span className={`h-2 w-2 rounded-full ${open ? "bg-emerald-400 shadow-[0_0_8px_#34d399]" : "animate-pulse bg-amber-400"}`} />
        {open ? "Tempo real conectado" : "Reconectando…"}
      </span>
      <div className="ml-auto flex items-center gap-2">
        <SystemOnlineToggle />
        <OnlineServiceToggle />
        <DocumentValidationToggle />
        <button onClick={() => setSound(!sound)} className="icon-btn" title="Alerta sonoro" data-testid="admin-sound-toggle">
          {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
        <button onClick={clearNew} className="icon-btn relative" title="Novas notificações" data-testid="admin-new-alerts">
          <Bell className="h-4 w-4" />
          {newCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-black" style={{ background: "var(--brand)" }} data-testid="admin-new-alerts-count">{newCount}</span>}
        </button>
        <span className="hidden text-sm text-slate-300 sm:inline" data-testid="admin-user-name">{user?.name}</span>
        <button onClick={logout} className="icon-btn" title="Sair" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /></button>
      </div>
    </header>
  );
};

export default function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  if (user === null) return <div className="admin-bg flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  return (
    <RealtimeProvider>
      <div className="admin-bg flex min-h-screen text-slate-200">
        <aside className={`fixed inset-y-0 left-0 z-30 w-64 border-r border-white/5 bg-[#0D1322] transition-transform lg:static lg:translate-x-0 ${menu ? "translate-x-0" : "-translate-x-full"}`}>
          <Sidebar onNavigate={() => setMenu(false)} />
        </aside>
        {menu && <div className="fixed inset-0 z-20 bg-black/60 lg:hidden" onClick={() => setMenu(false)} />}
        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onMenu={() => setMenu(true)} />
          <main className="flex-1 p-4 sm:p-8"><Outlet /></main>
        </div>
      </div>
    </RealtimeProvider>
  );
}
